// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import { cx } from '@linaria/core';
import React, { memo, isValidElement, useEffect } from 'react';

import { themeCssVar as theme } from '../../utils/theme';
import { BannerProps } from './types';
import styles from './styles';

import { IconNavigationClose } from '../../icons';

const Banner = (props: BannerProps) => {
  const {
    className,
    children,
    backgroundContainer,
    backgroundIconContainer,
    foregroundIcon = theme.color.iconPrimaryOverlay,
    foregroundBody,
    foregroundHeadline,
    title,
    headingLineClamp = 2,
    body,
    bodyLineClamp = 3,
    featureIcon,
    onDismiss,
    action,
    type = 'basic',
    enableKeyboardDismissal = true,
    ...restProps
  } = props;

  // Keydown event listener for keyboard dismissal
  useEffect(() => {
    if (!enableKeyboardDismissal) {
      return;
    }

    let hasFocus = false;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (hasFocus && event.key === 'Escape') {
        onDismiss?.();
      }
    };

    const handleFocusIn = () => {
      hasFocus = true;
    };

    const handleFocusOut = () => {
      hasFocus = false;
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableKeyboardDismissal, onDismiss]);

  const cssVariables = {
    [`--tui-banner-background-container`]: backgroundContainer,
    [`--tui-banner-background-icon-container`]: backgroundIconContainer,
    [`--tui-banner-foreground-headline`]: foregroundHeadline,
    [`--tui-banner-foreground-body`]: foregroundBody,
    [`--tui-banner-heading-line-clamp`]: headingLineClamp,
    [`--tui-banner-body-line-clamp`]: bodyLineClamp,
  } as React.CSSProperties;

  const style = cssVariables;

  const bannerProps = {
    className: cx(styles.container, className),
    style: style,
    'data-hero': type === 'hero' ? '' : undefined,
    'data-testid': 'banner',
  };

  return (
    <div {...restProps} {...bannerProps}>
      <div className={styles.content}>
        {featureIcon && (
          <div className={styles.featureIconContainer}>
            <div className={styles.featureIcon}>
              {isValidElement(featureIcon)
                ? featureIcon
                : React.createElement(featureIcon, {
                    fill: foregroundIcon,
                  })}
            </div>
          </div>
        )}
        <div className={styles.verticalContent}>
          <div className={styles.textContainer}>
            {title && (
              <div className={styles.headlineContainer}>
                <div className={styles.title}>{title}</div>
              </div>
            )}
            {body && <div className={styles.body}>{body}</div>}
            {children}
          </div>
          {action && <div className={styles.action}>{action}</div>}
        </div>
      </div>
      <div className={styles.dismissContainer} data-testid="dismiss-icon" onClick={onDismiss}>
        <div className={styles.dismissIcon}>
          <IconNavigationClose fill={theme.color.iconSecondary} />
        </div>
      </div>
    </div>
  );
};

export default memo(Banner);
